import { APPOINTMENT_STATUS_LABELS, APPOINTMENT_STATUSES, formatTime } from '@crm/shared';
import { Button, Card, CardContent, Label, Select, Spinner } from '@crm/ui';
import { ChevronRight, ClipboardList, Download, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ExportPdfButton, PageHeader, PrintButton } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { usePrintMode } from '@/lib/print-mode';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { AppointmentDialog } from '@/modules/schedule/components/appointment-dialog';
import { AppointmentStatusBadge } from '@/modules/schedule/components/status-badge';
import { type ReportFilters, reportParams, useAppointmentsReport, useKpis } from '../api';
import { KpiCards } from '../components/kpi-cards';
import { defaultFilters, ReportFiltersBar } from '../components/report-filters';

/** Quantas linhas do relatorio entram na tela por vez. */
const PAGINA = 50;

/** Pesquisa de atendimentos: a listagem detalhada do bloco 5. */
export function AppointmentsReportPage() {
  useDocumentTitle('Pesquisar atendimentos');

  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [status, setStatus] = useState('');

  const kpis = useKpis(filters);
  const report = useAppointmentsReport(filters, status || undefined);

  /**
   * Um periodo largo devolve milhares de atendimentos e pintar tudo de uma vez
   * trava a tela. Mostra-se um lote por vez; os totais do rodape continuam
   * vindo do servidor, sobre o periodo inteiro, e o CSV sai completo.
   */
  const [visiveis, setVisiveis] = useState(PAGINA);
  /** Atendimento aberto na modal da agenda. */
  const [openId, setOpenId] = useState<string | null>(null);
  const imprimindo = usePrintMode();
  const linhas = report.data?.data ?? [];
  // No papel a lista sai inteira: paginacao e coisa de tela.
  const linhasVisiveis = imprimindo ? linhas : linhas.slice(0, visiveis);

  // Trocou o filtro: a lista recomeca do primeiro lote.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reinicia a cada consulta nova
  useEffect(() => setVisiveis(PAGINA), [report.data]);

  const csvUrl = `/api/v1/reports/appointments.csv?${reportParams(filters, { status: status || undefined })}`;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ClipboardList />}
        title="Pesquisar atendimentos"
        description="Listagem e detalhamento dos atendimentos do periodo."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Atendimentos do periodo" />
            <Button asChild variant="outline">
              <a href={csvUrl} download>
                <Download className="h-4 w-4" />
                <span className="sm:hidden">CSV</span>
                <span className="hidden sm:inline">Exportar CSV</span>
              </a>
            </Button>
          </div>
        }
      />

      <KpiCards kpis={kpis.data} />

      {report.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(report.error)}</p>
      ) : null}

      <Card variant="flush">
        {/* Filtro e resultado no mesmo quadro; a tabela segue sem recuo. */}
        <CardContent className="space-y-4 pb-6">
          {/* A situacao e o filtro mais usado desta tela: fica a vista, junto
              do periodo, e nunca depois do botao de busca avancada. */}
          <ReportFiltersBar
            value={filters}
            onChange={setFilters}
            extra={
              <div className="col-span-2 space-y-2 lg:col-span-1">
                <Label htmlFor="report-status">Status</Label>
                <Select
                  id="report-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <option value="">Todos</option>
                  {APPOINTMENT_STATUSES.map((option) => (
                    <option key={option} value={option}>
                      {APPOINTMENT_STATUS_LABELS[option]}
                    </option>
                  ))}
                </Select>
              </div>
            }
          />
        </CardContent>
        <CardContent className="p-0">
          {report.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* No celular a tabela viraria rolagem lateral: cada atendimento
                  vira um cartao, com o que se le primeiro em cima. */}
              <ul className="divide-y divide-border md:hidden">
                {(report.data?.data.length ?? 0) === 0 ? (
                  <li className="px-4 py-10 text-center text-muted-foreground">
                    Nenhum atendimento no periodo.
                  </li>
                ) : (
                  linhasVisiveis.map((row) => (
                    <li key={row.id} className="flex items-center gap-2 px-4 py-3">
                      {/* A linha abre o atendimento: daqui o balcao ja remarca
                          ou fecha a comanda, sem procurar na agenda. */}
                      <button
                        type="button"
                        className="min-w-0 flex-1 space-y-0.5 text-left"
                        onClick={() => setOpenId(row.id)}
                      >
                        <span className="flex items-baseline justify-between gap-3">
                          <span className="min-w-0 truncate font-medium">{row.customerName}</span>
                          <span className="whitespace-nowrap font-semibold">
                            {formatCurrency(row.total)}
                          </span>
                        </span>
                        <span className="block truncate text-sm text-muted-foreground">
                          {formatDateTime(row.startAt)} - {row.services}
                        </span>
                        <AppointmentStatusBadge status={row.status} />
                      </button>

                      <ChevronRight
                        aria-hidden
                        className="h-5 w-5 shrink-0 text-muted-foreground"
                      />
                    </li>
                  ))
                )}

                {report.data && report.data.data.length > 0 ? (
                  <li className="flex items-baseline justify-between gap-3 bg-muted/40 px-4 py-3">
                    <span className="font-medium">{report.data.totals.count} atendimentos</span>
                    <span className="font-semibold">
                      {formatCurrency(report.data.totals.total)}
                    </span>
                  </li>
                ) : null}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Quando</th>
                      <th className="px-4 py-3 font-medium">Unidade</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Profissional</th>
                      <th className="px-4 py-3 font-medium">Servicos</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.data?.data.length ?? 0) === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                          Nenhum atendimento no periodo.
                        </td>
                      </tr>
                    ) : (
                      linhasVisiveis.map((row) => (
                        <tr
                          key={row.id}
                          className="cursor-pointer border-t border-border hover:bg-muted/50"
                          onClick={() => setOpenId(row.id)}
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            {formatDateTime(row.startAt)} - {formatTime(row.endAt)}
                          </td>
                          <td className="px-4 py-3">{row.unitName}</td>
                          <td className="px-4 py-3">{row.customerName}</td>
                          <td className="px-4 py-3">{row.professionalName ?? '-'}</td>
                          <td className="px-4 py-3">{row.services}</td>
                          <td className="px-4 py-3">
                            <AppointmentStatusBadge status={row.status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-medium">
                            {formatCurrency(row.total)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {report.data && report.data.data.length > 0 ? (
                    <tfoot className="border-t border-border bg-muted/40">
                      <tr>
                        <td className="px-4 py-3 font-medium" colSpan={6}>
                          {report.data.totals.count} atendimentos
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                          {formatCurrency(report.data.totals.total)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>

              {linhas.length > linhasVisiveis.length || report.data?.truncated ? (
                <div className="flex flex-col items-center gap-1 border-t border-border px-4 py-4">
                  {linhas.length > linhasVisiveis.length ? (
                    <Button
                      variant="ghost"
                      className="w-full text-primary print:hidden sm:w-auto"
                      onClick={() => setVisiveis((atual) => atual + PAGINA)}
                    >
                      <Plus className="h-4 w-4" />
                      Mostrar mais
                    </Button>
                  ) : null}

                  {/* Fica no PDF de proposito: quem le a folha precisa saber
                      que ela nao traz o periodo inteiro. */}
                  {report.data?.truncated ? (
                    <p className="text-xs text-muted-foreground">
                      O periodo tem mais atendimentos do que cabe nesta consulta.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {openId ? <AppointmentDialog id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}
